import React from "react";
import {
  FaCheckCircle,
  FaClock,
  FaBan,
  FaExclamationTriangle,
} from "react-icons/fa";

/**
 * Top-of-page banner for non-sent invoice states. Renders null
 * for `sent` (the default payable state) — the payment form +
 * line items speak for themselves.
 *
 *   paid      → green, check icon, "Paid on <date>"
 *   overdue   → amber, warning icon, "Overdue — payment still accepted"
 *   cancelled → gray, ban icon, "Cancelled" + "no longer payable"
 *   draft     → amber/info, clock icon, "Draft — not yet sent"
 *
 * Colors come from the `.pay-invoice-banner--*` modifier classes
 * in pay.css so themes can be tuned in one place.
 */
export function InvoiceStatusBanner({ invoice }) {
  if (!invoice) return null;
  const status = invoice.status;
  if (status === "sent") return null;

  let variant;
  let Icon;
  let title;
  let sub;
  switch (status) {
    case "paid": {
      variant = "success";
      Icon = FaCheckCircle;
      title = "Paid";
      sub =
        invoice.paidAt && invoice.paymentTxHash
          ? `Settled on ${formatDate(invoice.paidAt)}`
          : "This invoice has been settled.";
      break;
    }
    case "overdue": {
      variant = "warning";
      Icon = FaExclamationTriangle;
      title = "Overdue";
      sub = invoice.dueDate
        ? `Was due ${formatDate(invoice.dueDate)} — payment is still accepted.`
        : "Payment is past due.";
      break;
    }
    case "cancelled": {
      variant = "muted";
      Icon = FaBan;
      title = "Cancelled";
      sub = invoice.cancelledAt
        ? `This invoice was cancelled on ${formatDate(invoice.cancelledAt)} and can no longer be paid.`
        : "This invoice was cancelled and can no longer be paid.";
      break;
    }
    case "draft": {
      variant = "info";
      Icon = FaClock;
      title = "Draft";
      sub = "This invoice hasn't been sent yet.";
      break;
    }
    default:
      return null;
  }
  return (
    <div
      className={`pay-invoice-banner pay-invoice-banner--${variant}`}
      role="status"
    >
      <Icon size={18} aria-hidden="true" />
      <div>
        <div className="pay-invoice-banner__title">{title}</div>
        <div className="pay-invoice-banner__sub">{sub}</div>
      </div>
    </div>
  );
}

function formatDate(ms) {
  if (!ms || !Number.isFinite(Number(ms))) return "";
  try {
    return new Date(Number(ms)).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return new Date(Number(ms)).toISOString().slice(0, 10);
  }
}
